import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'

const ease: [number, number, number, number] = [0.16, 1, 0.3, 1]

const socials = [
  {
    name: 'LinkedIn',
    href: 'https://www.linkedin.com/company/jpdz/',
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
      </svg>
    )
  },
  {
    name: 'Instagram',
    href: 'https://www.instagram.com/jpdz.app/',
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="2" y="2" width="20" height="20" rx="5" />
        <circle cx="12" cy="12" r="4" />
        <circle cx="18" cy="6" r="1" fill="currentColor" />
      </svg>
    )
  },
  {
    name: 'X',
    href: 'https://x.com/JeopardizeP',
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
      </svg>
    )
  }
]

const navLinks = [
  { to: '/about', label: 'About' },
  { to: '/developers', label: 'Developers' },
  { to: '/login', label: 'Sign In' },
  { to: '/chat', label: 'Open Jpdz' },
]

const legalLinks = [
  { to: '/privacy', label: 'Privacy Policy' },
  { to: '/terms', label: 'Terms of Service' },
  { href: 'mailto:ratnam@jpdz.app', label: 'Contact' },
]

const themes = {
  light: {
    footer: 'border-t border-[#211d18]/8 bg-[#f3ecdf]',
    accent: 'bg-[#6e1f1b]/35',
    brand: 'text-[#211d18] hover:text-[#6e1f1b]',
    label: 'text-[#6e1f1b]/55',
    body: 'text-[#5f554b]',
    link: 'text-[#5f554b] hover:text-[#211d18]',
    underline: 'bg-[#6e1f1b]/40',
    socialBtn: 'border-[#211d18]/10 bg-[#eee8dc] text-[#5f554b] hover:border-[#6e1f1b]/25 hover:text-[#211d18]',
    divider: 'border-[#211d18]/8',
    muted: 'text-[#6b6157]/70',
    mutedHover: 'text-[#6b6157]/70 hover:text-[#211d18]',
  },
  dark: {
    footer: 'border-t border-[#eee8dc]/6 bg-[#1a1714]',
    accent: 'bg-[#6e1f1b]/50',
    brand: 'text-[#eee8dc] hover:text-[#c8a88a]',
    label: 'text-[#c8a88a]/50',
    body: 'text-[#eee8dc]/50',
    link: 'text-[#eee8dc]/50 hover:text-[#eee8dc]',
    underline: 'bg-[#c8a88a]/30',
    socialBtn: 'border-[#eee8dc]/8 bg-[#211d18] text-[#eee8dc]/50 hover:border-[#6e1f1b]/30 hover:text-[#eee8dc]',
    divider: 'border-[#eee8dc]/6',
    muted: 'text-[#eee8dc]/35',
    mutedHover: 'text-[#eee8dc]/35 hover:text-[#eee8dc]',
  },
}

export function Footer({ variant = 'light' }: { variant?: 'light' | 'dark' }) {
  const year = new Date().getFullYear()
  const t = themes[variant]

  return (
    <footer className={t.footer}>
      <div className="mx-auto max-w-[1480px] px-4 py-20 sm:px-8 sm:py-24">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease }}
          className={`mb-16 h-[2px] w-14 ${t.accent}`}
        />

        <div className="grid gap-12 sm:grid-cols-2 lg:grid-cols-[1.3fr_0.6fr_0.6fr_0.8fr]">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease }}
          >
            <Link to="/" className={`mb-3 inline-block font-serif text-[1.75rem] transition-colors duration-300 ${t.brand}`}>
              Jpdz Labs
            </Link>
            <p className={`mb-5 font-body text-[10px] uppercase tracking-[0.22em] ${t.label}`}>
              Frontier software and research
            </p>
            <p className={`max-w-sm font-body text-[15px] leading-[1.8] ${t.body}`}>
              Products for retrieval, memory, chat, dashboards, and PKM, a model built from scratch with a novel architecture.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.06, ease }}
          >
            <span className={`mb-5 block font-body text-[10px] uppercase tracking-[0.22em] ${t.label}`}>
              Navigate
            </span>
            <div className="space-y-3.5">
              {navLinks.map(link => (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`group block font-body text-[15px] transition-colors duration-300 ${t.link}`}
                >
                  <span className="relative">
                    {link.label}
                    <span className={`absolute -bottom-0.5 left-0 h-px w-0 transition-all duration-500 group-hover:w-full ${t.underline}`} />
                  </span>
                </Link>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1, ease }}
          >
            <span className={`mb-5 block font-body text-[10px] uppercase tracking-[0.22em] ${t.label}`}>
              Legal
            </span>
            <div className="space-y-3.5">
              {legalLinks.map(link => {
                const Tag = 'to' in link ? Link : 'a'
                const props = 'to' in link ? { to: link.to } : { href: link.href }
                return (
                  <Tag
                    key={link.label}
                    {...(props as any)}
                    className={`group block font-body text-[15px] transition-colors duration-300 ${t.link}`}
                  >
                    <span className="relative">
                      {link.label}
                      <span className={`absolute -bottom-0.5 left-0 h-px w-0 transition-all duration-500 group-hover:w-full ${t.underline}`} />
                    </span>
                  </Tag>
                )
              })}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.14, ease }}
          >
            <span className={`mb-5 block font-body text-[10px] uppercase tracking-[0.22em] ${t.label}`}>
              Connect
            </span>
            <div className="flex flex-wrap gap-2.5">
              {socials.map((social) => (
                <motion.a
                  key={social.name}
                  href={social.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ y: -3, scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className={`flex h-11 w-11 items-center justify-center border transition-all duration-300 ${t.socialBtn}`}
                  aria-label={social.name}
                >
                  {social.icon}
                </motion.a>
              ))}
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2, ease }}
          className={`mt-16 flex flex-col items-center justify-between gap-4 border-t pt-8 text-center md:flex-row md:text-left ${t.divider}`}
        >
          <p className={`font-body text-[13px] ${t.muted}`}>
            &copy; {year} Jpdz Labs. All rights reserved.
          </p>
          <div className="flex gap-6">
            <Link to="/privacy" className={`font-body text-[13px] transition-colors duration-300 ${t.mutedHover}`}>
              Privacy
            </Link>
            <Link to="/terms" className={`font-body text-[13px] transition-colors duration-300 ${t.mutedHover}`}>
              Terms
            </Link>
          </div>
        </motion.div>
      </div>
    </footer>
  )
}
